import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { CommunityDTO } from '../../models/community'
import { useContext, useEffect, useState } from 'react'
import CommunitiesService from '../../services/CommunitiesService'
import { isAdmin, isCurrentUser } from '../../helpers/utils'
import { AppRoutes } from '../../helpers/routes'
import CommunityCard from './CommunityCard'
import CustomButton from '../../components/Button'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import { PostDTO } from '../../models/post'
import CustomPaper from '../../containers/Paper'
import Post from '../home/Post'
import CustomModal from '../../containers/Modal'
import PostModal from '../../components/Modal/PostModal'
import NoResults from '../../containers/NoResults'
import { CircularProgress } from '@mui/material'
import GoBackButton from '../../components/GoBackButton'
import { PageContext } from '../../context/PageContext'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import { UserDTO } from '../../models/users'
import UserService from '../../services/UserService'

const CommunityPage = () => {
    const { state } = useLocation()
    const { communityId } = useParams<{ communityId: string }>()
    const [community, setCommunity] = useState<CommunityDTO>()
    const [isNewPostOpen, setIsNewPostOpen] = useState<boolean>(false)
    const [posts, setPosts] = useState<PostDTO[]>()
    const [mutate, setMutate] = useState<boolean>(true)
    const userIsMember = () => community && community.members.some((user) => isCurrentUser(user))
    const userIsCommunityAdmin = () =>
        community && community.administrators.some((user) => isCurrentUser(user))
    const navigate = useNavigate()
    const { smallScreen } = useContext(PageContext)
    const [user, setUser] = useState<UserDTO>()
    const location = useLocation()

    useEffect(() => {
        if (!communityId) return
        new CommunitiesService().getCommunityById(communityId).then((response) => {
            if (response.status === 200) setCommunity(response.data)
        })
        new UserService().getCurrentUser().then((response) => {
            if (response.status === 200) setUser(response.data)
        })
    }, [communityId, mutate])

    useEffect(() => {
        if (!community || !community.members.some((user) => isCurrentUser(user))) return
        new CommunitiesService().getCommunityPosts(community.id).then((response) => {
            if (response.status === 200) setPosts(response.data)
        })
    }, [community, mutate])

    const isCommunityAdmin = () => {
        return community?.administrators.some((x) => x.id === user?.id)
    }

    return (
        <>
            {community ? (
                <div className="flex flex-col gap-2 w-full">
                    {state?.goBackPath && (
                        <div className="w-fit flex gap-2 items-center">
                            <GoBackButton
                                size={smallScreen ? 'small' : 'medium'}
                                navigate={() => navigate(state?.goBackPath)}
                            />
                            <span className="text-lg md:text-xl font-semibold text-secondary">
                                Volver atrás
                            </span>
                        </div>
                    )}
                    {!isAdmin() ? (
                        <div className="flex flex-col gap-2 md:gap-4 w-full">
                            <CommunityCard
                                community={community}
                                userIsCommunityAdmin={userIsCommunityAdmin()}
                                userIsMember={!!userIsMember()}
                                singlePage
                                mutate={mutate}
                                setMutate={setMutate}
                            />
                            {mutate !== undefined && userIsMember() && (
                                <>
                                    <div className="flex flex-col md:flex-row gap-3 md:gap-6 mt-1 md:mt-2 justify-between items-start md:items-center">
                                        <span className="text-xl md:text-2xl">Publicaciones</span>
                                        <div className="flex gap-2 md:gap-3">
                                            {isCommunityAdmin() && (
                                                <CustomButton
                                                    text="Publicaciones Pendientes"
                                                    className="h-[36px] text-xs md:text-base"
                                                    onClick={() => {
                                                        navigate(
                                                            AppRoutes.pendingPosts.replace(
                                                                ':communityId',
                                                                community.id,
                                                            ),
                                                            {
                                                                state: {
                                                                    goBackPath: location?.pathname,
                                                                },
                                                            },
                                                        )
                                                    }}
                                                    startIcon={<PendingActionsIcon />}
                                                    secondary
                                                />
                                            )}
                                            <CustomButton
                                                text="Crear Publicación"
                                                className="h-[36px] text-xs md:text-base"
                                                onClick={() => setIsNewPostOpen(true)}
                                                startIcon={<AddCircleOutlineIcon />}
                                                secondary
                                            />
                                        </div>
                                    </div>
                                    {posts ? (
                                        <div className="text-black flex flex-col gap-4">
                                            {posts.length > 0 ? (
                                                posts.map((post) => (
                                                    <CustomPaper key={post.id}>
                                                        <Post
                                                            info={post}
                                                            setMutate={setMutate}
                                                            canDelete={userIsCommunityAdmin()}
                                                        />
                                                    </CustomPaper>
                                                ))
                                            ) : (
                                                <CustomPaper>
                                                    <NoResults
                                                        title="No se encontraron publicaciones de la comunidad"
                                                        subtitle="¡Sé el primero en crear una!"
                                                    />
                                                </CustomPaper>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="flex w-full justify-center">
                                            <CircularProgress
                                                className="m-2 md:m-5 text-primary"
                                                size={64}
                                            />
                                        </div>
                                    )}
                                    <CustomModal
                                        isOpen={isNewPostOpen}
                                        handleClose={() => {
                                            setIsNewPostOpen(false)
                                            setMutate((prev) => !prev)
                                        }}
                                    >
                                        <PostModal
                                            handleClose={() => {
                                                setIsNewPostOpen(false)
                                                setMutate((prev) => !prev)
                                            }}
                                            communityId={community.id}
                                            communityAdmin={isCommunityAdmin()}
                                        />
                                    </CustomModal>
                                </>
                            )}
                        </div>
                    ) : (
                        <Navigate to={AppRoutes.login} />
                    )}
                </div>
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            )}
        </>
    )
}

export default CommunityPage
